import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useI18n, AVAILABLE_LANGUAGES } from './I18nContext';

/**
 * Language switcher — drop into Header.js next to the primary nav.
 *
 * Why SVG flags (not emoji): the previous version used 🇺🇸 / 🇪🇸 which
 * Windows browsers render as plain letter pairs ("US" / "ES") because
 * Windows ships without the regional-indicator emoji font. flagcdn.com
 * serves crisp SVG flags by ISO country code and is CDN-cached, so we
 * pay zero render cost after first load.
 *
 * Variants:
 *   <LanguageSwitcher />          full label (desktop)
 *   <LanguageSwitcher compact />  flag only (mobile-friendly)
 */
const FLAG_BASE = 'https://flagcdn.com';

/** Render a single small SVG flag from flagcdn (~1KB each, CDN-cached). */
const Flag = ({ code, alt, size = 20 }) => (
  <img
    src={`${FLAG_BASE}/w40/${code}.png`}
    srcSet={`${FLAG_BASE}/w40/${code}.png 1x, ${FLAG_BASE}/w80/${code}.png 2x`}
    width={size}
    height={Math.round(size * 0.75)}
    alt={alt || ''}
    loading="lazy"
    decoding="async"
    style={{
      display: 'inline-block',
      borderRadius: '2px',
      objectFit: 'cover',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
      verticalAlign: 'middle',
      flexShrink: 0,
    }}
  />
);

const LanguageSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = AVAILABLE_LANGUAGES.find((l) => l.code === lang) || AVAILABLE_LANGUAGES[0];

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg hover:bg-gray-100 text-sm font-medium text-gray-700 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Change language — current: ${current.nativeLabel}`}
        title={current.nativeLabel}
      >
        {compact ? (
          <>
            <Flag code={current.flagCode} alt={current.nativeLabel} size={22} />
            <ChevronDown
              className={`w-3 h-3 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </>
        ) : (
          <>
            <Flag code={current.flagCode} alt={current.nativeLabel} size={22} />
            <span className="hidden sm:inline">{current.nativeLabel}</span>
            <ChevronDown
              className={`w-3 h-3 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </>
        )}
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Select language"
          // notranslate class + translate="no" attribute: prevent Google
          // Translate from turning the native-language labels into the
          // active language (we want each language to ALWAYS appear in
          // its own script — Russian users still need to see "中文" not
          // "Китайский").
          className="notranslate absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden"
          style={{
            // Cap the height on small viewports so 14 languages don't
            // push the dropdown below the fold — the user can scroll
            // inside the list.
            maxHeight: 'min(70vh, 480px)',
            overflowY: 'auto',
          }}
          translate="no"
        >
          {/* Header tag */}
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 border-b border-gray-100 flex items-center gap-1.5">
            <Globe className="w-3 h-3" aria-hidden="true" />
            <span>Choose language</span>
          </div>

          {AVAILABLE_LANGUAGES.map((l) => {
            const isActive = l.code === lang;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors text-left border-b border-gray-50 last:border-b-0 ${
                  isActive
                    ? 'bg-amber-50 text-amber-900 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                lang={l.code}
              >
                <Flag code={l.flagCode} alt="" size={22} />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{l.nativeLabel}</div>
                  {l.label !== l.nativeLabel && (
                    <div className="text-[11px] text-gray-400 truncate" translate="no">
                      {l.label}
                    </div>
                  )}
                </div>
                {isActive && <Check className="w-4 h-4 text-amber-600 flex-shrink-0" aria-hidden="true" />}
              </button>
            );
          })}

          <div className="px-3 py-2 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100 leading-tight">
            Translations powered by Google Translate. Some text may vary.
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
